/**
 * Resolve the delivery label that actually applies to an order.
 * "ADD" orders (additional deliveries on an existing main order) inherit
 * their delivery label from the main order rather than carrying their own.
 * @param {Object} order - order/farmorder object
 * @param {Array<Object>} orders - full list of orders, used to find the main order
 * @returns {string|undefined} the resolved delivery label code
 */
export const resolveOrderDeliveryLabel = (order, orders) => {
  let deliveryLabel = order?.deliveryLabel;

  if (deliveryLabel === "ADD" && order?.mainOrderId) {
    const mainOrder = orders?.find((o) => o.id === order.mainOrderId);
    if (mainOrder) {
      deliveryLabel = mainOrder.deliveryLabel;
    }
  }

  return deliveryLabel;
};

/**
 * Resolve the country code that applies to an order, used to look up the
 * lead time configured for its delivery label. Acquisition orders have no
 * customer; their country comes from the lead instead.
 * @param {Object} order - order/farmorder object
 * @param {Array<Object>} customers - full list of customers
 * @param {Array<Object>} leads - full list of leads
 * @returns {string|undefined} the resolved country code
 */
export const resolveOrderCountry = (order, customers, leads) => {
  if (order?.type === "acquisition") {
    return leads?.find((l) => l.leadId === order.leadId)?.country;
  }

  return customers?.find((c) => c.id === order?.farmorderCustomerId)?.country;
};

/**
 * Resolve the lead time (in days) between picking/packing and delivery for
 * an order, and the resulting picking date.
 *
 * This bundles the country + delivery label lookup (including the "ADD"
 * main-order fallback) that previously lived duplicated across
 * orderpicking, stock and forecast views.
 *
 * @param {Object} order - order/farmorder object
 * @param {Object} deps
 * @param {Array<Object>} deps.orders - full list of orders (for ADD main-order lookup)
 * @param {Array<Object>} deps.customers - full list of customers
 * @param {Array<Object>} deps.leads - full list of leads
 * @param {Function} deps.getDeliveryLabelLeadDays - from useCountries(), (countryCode, deliveryLabel) => number
 * @returns {number} leadDays, defaults to 1 when nothing is configured
 */
export const resolveOrderLeadDays = (order, { orders, customers, leads, getDeliveryLabelLeadDays }) => {
  const country = resolveOrderCountry(order, customers, leads);
  const deliveryLabel = resolveOrderDeliveryLabel(order, orders);

  return getDeliveryLabelLeadDays(country, deliveryLabel);
};
